import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import { api } from "./ApiClient";

import { AuthHelper } from "../helpers/auth.helper";

import { closeModal } from "../store/modal.slice";

import { HMICreateEmployee, HMIImportEmployeesFromCsv, HMIInviteEmployee, HMIBulkInviteEmployees, HMIUpdateEmployee, IEmployeeListItem, TEmployeeEntitlementsView } from "../types/services.interfaces/employees-cax";

export const useGetEmployees = (entitlementsView: TEmployeeEntitlementsView) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IEmployeeListItem[]> => {
        const res = await api.get(`/employees-cax/list/${entitlementsView}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['employees-list', entitlementsView],
        queryFn,
    });
};

export const useGetEmployee = (userId: string | null) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IEmployeeListItem> => {
        const res = await api.get(`/employees-cax/details/${userId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['employees-details', userId],
        enabled: !!userId,
        queryFn,
    });
};

export const useCreateEmployee = () => {
    AuthHelper.presetPrivateRequest(api);

    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMICreateEmployee): Promise<void> => {
        const res = await api.post('/employees-cax/create', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Employee created successfully', {
                duration: 4000,
                position: 'top-right',
            });

            dispatch(closeModal("MANAGE_EMPLOYEE_MODAL"));
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['employees-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useUpdateEmployee = () => {
    AuthHelper.presetPrivateRequest(api);

    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIUpdateEmployee): Promise<void> => {
        const res = await api.post('/employees-cax/update', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Employee updated successfully', {
                duration: 4000,
                position: 'top-right',
            });

            dispatch(closeModal("MANAGE_EMPLOYEE_MODAL"));
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['employees-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useImportEmployeesFromCsv = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIImportEmployeesFromCsv): Promise<{ message: string }> => {
        const formData = new FormData();
        formData.append('file', data.payload.file);

        const res = await api.post('/employees-cax/import-csv', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: (data: { message: string }) => {
            toast.success(data.message, {
                duration: 6000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 6000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['employees-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useInviteEmployee = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIInviteEmployee): Promise<void> => {
        const res = await api.post(`/employees-cax/invite/${data.userId}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Invitation sent successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['employees-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useBulkInviteEmployees = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIBulkInviteEmployees): Promise<void> => {
        const res = await api.post('/employees-cax/bulk-invite', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Invitations sent successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['employees-list'] });
            variables.setIsProcessing(false);
        },
    });
};